import { useMutation } from "@apollo/client";
import { CheckIcon } from "@heroicons/react/solid";
import asanaLogo from "@static/img/asana-logo.png";
import clickupLogo from "@static/img/clickup.png";
import firefliesLogo from "@static/img/fireflies-logo.png";
import githubLogo from "@static/img/github.png";
import granolaLogo from "@static/img/granola-logo.png";
import hubspotLogo from "@static/img/hubspot.png";
import jiraLogo from "@static/img/jira.png";
import readAiLogo from "@static/img/read-ai-logo.svg";
import salesforceLogo from "@static/img/salesforce.png";
import zoomLogo from "@static/img/zoom.png";
import { FormEvent, useState } from "react";
import { GetAccountQuery } from "types/graphql-schema";

import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Link from "@components/link/link";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { assertNonNull, getAssetUrl } from "@helpers/helpers";

import getAccountQuery from "../graphql/get-account-query";
import removeAsanaIntegrationMutation from "../graphql/remove-asana-integration-mutation";
import removeClickupIntegrationMutation from "../graphql/remove-clickup-integration-mutation";
import removeGitHubIntegrationMutation from "../graphql/remove-github-integration-mutation";
import removeHubspotIntegrationMutation from "../graphql/remove-hubspot-integration-mutation";
import removeJiraIntegrationMutation from "../graphql/remove-jira-integration-mutation";
import removePersonalTranscriptSyncCredentialMutation from "../graphql/remove-personal-transcript-sync-credential-mutation";
import removeReadAiIntegrationMutation from "../graphql/remove-read-ai-integration-mutation";
import removeSalesforceIntegrationMutation from "../graphql/remove-salesforce-integration-mutation";
import removeZoomIntegrationMutation from "../graphql/remove-zoom-integration-mutation";
import savePersonalTranscriptSyncCredentialMutation from "../graphql/save-personal-transcript-sync-credential-mutation";
import McpIntegrationSection from "./mcp-integration-section";

type PersonalTranscriptSyncProvider = "granola" | "fireflies";

type PersonalTranscriptSyncIntegration = {
  provider: PersonalTranscriptSyncProvider;
  name: string;
  logoAssetUrl: string | URL;
  logoAlt: string;
  description: JSX.Element;
  connected?: boolean | null;
};

export const IntegrationRow = ({
  logoAssetUrl,
  logoAlt,
  text,
  buttons,
}: {
  logoAssetUrl: string | URL;
  logoAlt: string;
  text: JSX.Element;
  buttons: JSX.Element;
}) => (
  <li className="flex gap-8 py-6 border-0 border-b border-gray-200">
    <div className="col font-medium mb-4 flex items-start w-28 shrink-0">
      <div className="border rounded flex justify-center items-center h-24 w-28 p-4">
        <img
          src={getAssetUrl(logoAssetUrl)}
          alt={logoAlt}
          className="object-contain h-16"
        />
      </div>
    </div>
    <div className="flex-1">{text}</div>
    <div className="w-48 shrink-0 flex flex-col gap-2 items-center">
      {buttons}
    </div>
  </li>
);

const PersonalApiTokenModal = ({
  integration,
  isSaving,
  onClose,
  onSave,
}: {
  integration: PersonalTranscriptSyncIntegration;
  isSaving: boolean;
  onClose: () => void;
  onSave: (apiKey: string) => void;
}) => {
  const [apiKey, setApiKey] = useState("");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    onSave(apiKey);
  };
  const title = integration.connected
    ? `Update ${integration.name} API key`
    : `Connect to ${integration.name}`;

  return (
    <Modal open onClose={onClose}>
      <form onSubmit={handleSubmit} className="p-6">
        <ModalTitle onClose={onClose}>{title}</ModalTitle>
        <div className="mt-6 space-y-4">
          <p className="text-sm text-gray-600">
            Enter your personal {integration.name} API key. Topicflow stores it
            for your active organizations and uses it to import your meeting
            transcripts.
          </p>
          <Input
            autoFocus
            label="API key"
            name={`${integration.provider}-api-key`}
            type="password"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            autocomplete="off"
          />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            type="submit"
            theme={buttonTheme.primary}
            disabled={isSaving || !apiKey.trim()}
          >
            Save API key
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const Integrations = ({ me }: { me: GetAccountQuery["me"] }) => {
  const account = assertNonNull(me);
  const organization = account.organizations.edges.find(
    (edge) => edge?.node && !edge.node.personal,
  )?.node;
  const [tokenModalIntegration, setTokenModalIntegration] =
    useState<PersonalTranscriptSyncIntegration | null>(null);
  const [removeAsanaIntegration] = useMutation(removeAsanaIntegrationMutation);
  const [removeGitHubIntegration] = useMutation(
    removeGitHubIntegrationMutation,
    { refetchQueries: [getAccountQuery] },
  );
  const [removeJiraIntegration] = useMutation(removeJiraIntegrationMutation);
  const [removeClickupIntegration] = useMutation(
    removeClickupIntegrationMutation,
  );
  const [removeSalesforceIntegration] = useMutation(
    removeSalesforceIntegrationMutation,
  );
  const [removeHubspotIntegration] = useMutation(
    removeHubspotIntegrationMutation,
  );
  const [removeReadAiIntegration] = useMutation(
    removeReadAiIntegrationMutation,
  );
  const [removeZoomIntegration] = useMutation(removeZoomIntegrationMutation);
  const [savePersonalTranscriptSyncCredential, { loading: isSavingToken }] =
    useMutation(savePersonalTranscriptSyncCredentialMutation, {
      refetchQueries: [getAccountQuery],
    });
  const [removePersonalTranscriptSyncCredential] = useMutation(
    removePersonalTranscriptSyncCredentialMutation,
    { refetchQueries: [getAccountQuery] },
  );

  const personalTranscriptSyncIntegrations: PersonalTranscriptSyncIntegration[] =
    [
      {
        provider: "granola",
        name: "Granola",
        logoAlt: "Granola",
        logoAssetUrl: granolaLogo,
        connected: account.hasGranolaPersonalApiToken,
        description: (
          <>
            <p>Import your Granola meeting transcripts into Topicflow.</p>
            <ul className="list-disc ml-4 py-2 italic">
              <li>
                Import Granola transcripts into matching Topicflow meetings
              </li>
              <li>Create Topicflow topics from Granola meeting notes</li>
            </ul>
          </>
        ),
      },
      {
        provider: "fireflies",
        name: "Fireflies",
        logoAlt: "Fireflies",
        logoAssetUrl: firefliesLogo,
        connected: account.hasFirefliesPersonalApiToken,
        description: (
          <>
            <p>Import your Fireflies meeting transcripts into Topicflow.</p>
            <ul className="list-disc ml-4 py-2 italic">
              <li>
                Import Fireflies transcripts into matching Topicflow meetings
              </li>
              <li>Create Topicflow topics from Fireflies meeting summaries</li>
            </ul>
          </>
        ),
      },
    ];

  const handleRemoveZoomIntegration = () => {
    removeZoomIntegration();
  };
  const handleRemoveAsanaIntegration = () => {
    removeAsanaIntegration({});
  };
  const handleRemoveGitHubIntegration = () => {
    removeGitHubIntegration({
      onCompleted: () => {
        successNotificationVar({ title: "GitHub integration removed" });
      },
    });
  };

  const handleRemoveJiraIntegration = () => {
    removeJiraIntegration({});
  };

  const handleRemoveClickupIntegration = () => {
    removeClickupIntegration({});
  };
  const handleRemoveSalesforceIntegration = () => {
    removeSalesforceIntegration({});
  };

  const handleRemoveHubspotIntegration = () => {
    removeHubspotIntegration({});
  };

  const handleRemoveReadAiIntegration = () => {
    removeReadAiIntegration({});
  };

  const handleSavePersonalTranscriptSyncCredential = (apiKey: string) => {
    if (!tokenModalIntegration) {
      return;
    }
    savePersonalTranscriptSyncCredential({
      variables: {
        provider: tokenModalIntegration.provider,
        apiKey,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${tokenModalIntegration.name} API key saved`,
        });
        setTokenModalIntegration(null);
      },
      onError: (error) => {
        errorNotificationVar({ title: error.message });
      },
    });
  };

  const handleRemovePersonalTranscriptSyncCredential = (
    integration: PersonalTranscriptSyncIntegration,
  ) => {
    removePersonalTranscriptSyncCredential({
      variables: {
        provider: integration.provider,
      },
      onCompleted: () => {
        successNotificationVar({
          title: `${integration.name} API key removed`,
        });
      },
      onError: (error) => {
        errorNotificationVar({ title: error.message });
      },
    });
  };

  const renderPersonalTranscriptSyncButtons = (
    integration: PersonalTranscriptSyncIntegration,
  ) =>
    integration.connected ? (
      <>
        <Button
          theme={buttonTheme.green}
          rounded
          className="flex"
          onClick={() => setTokenModalIntegration(integration)}
        >
          <CheckIcon className="text-green-500 size-5" />
          Connected
        </Button>
        <Button
          theme={buttonTheme.text}
          onClick={() => {
            handleRemovePersonalTranscriptSyncCredential(integration);
          }}
          className="font-light text-xs py-1 px-2 flex"
        >
          Disconnect from {integration.name}
        </Button>
      </>
    ) : (
      <Button rounded onClick={() => setTokenModalIntegration(integration)}>
        Connect to {integration.name}
      </Button>
    );

  return (
    <div>
      {tokenModalIntegration && (
        <PersonalApiTokenModal
          integration={tokenModalIntegration}
          isSaving={isSavingToken}
          onClose={() => setTokenModalIntegration(null)}
          onSave={handleSavePersonalTranscriptSyncCredential}
        />
      )}
      <div className="pb-2">
        <h2 className="text-2xl font-medium text-gray-800">
          Personal Integrations
        </h2>
        {organization && (
          <div className="text-sm">
            You can also{" "}
            <Link
              to={`/settings/organization/${organization.id}/integrations`}
              className="font-medium text-blue-600 hover:underline"
            >
              set up organization integrations
            </Link>
          </div>
        )}
      </div>
      <McpIntegrationSection />
      <h3 className="text-lg font-medium text-gray-800 pt-4">Connected apps</h3>
      <ul className="bg-white py-4" aria-label="Integration list">
        <IntegrationRow
          logoAlt={"Zoom"}
          logoAssetUrl={zoomLogo}
          text={
            <>
              <p>
                Add Zoom meetings automatically to meetings you create in
                Topicflow.
              </p>
              <p className="font-bold uppercase py-2 text-gray-400">Note:</p>
              <ul className="list-disc ml-4 text-gray-400">
                <li>
                  An administrator for your Zoom account must pre-approve our{" "}
                  <a
                    href="https://marketplace.zoom.us/apps/o5pQpYQeT5CCiZcXtdY73w"
                    title="Topicflow on Zoom Marketplace"
                    className="text-blue-600"
                  >
                    Zoom Marketplace app
                  </a>
                  .
                </li>
              </ul>
            </>
          }
          buttons={
            account.hasZoomAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveZoomIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveZoomIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Zoom
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/zoom?next=/account/integrations`}>
                Connect to Zoom
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Asana"}
          logoAssetUrl={asanaLogo}
          text={
            <>
              <p>Sync your Topicflow action items into Asana.</p>
            </>
          }
          buttons={
            account.hasAsanaAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveAsanaIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveAsanaIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Asana
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/asana?next=/account/integrations`}>
                Connect to Asana
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Github"}
          logoAssetUrl={githubLogo}
          text={
            <>
              <p>Connect your Topicflow and GitHub accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Preview GitHub issues and pull requests in Topicflow meeting
                  notes
                </li>
                <li>
                  Search for GitHub issues and pull requests from the Explorer,
                  and embed lists in meeting notes
                </li>
                <li>Link GitHub metrics to Topicflow KPIs</li>
              </ul>
            </>
          }
          buttons={
            account.hasGithubAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveGitHubIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
              </>
            ) : (
              <Button
                rounded
                url={`https://github.com/login/oauth/authorize?client_id=${window.githubAppClientId}`}
              >
                Connect to Github
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Jira"}
          logoAssetUrl={jiraLogo}
          text={
            <>
              <p>Connect your Topicflow and Jira accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>Preview Jira issues in Topicflow meeting notes</li>
                <li>
                  Search for Jira issues from the Explorer, and embed lists in
                  meeting notes
                </li>
              </ul>
            </>
          }
          buttons={
            account.hasJiraAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveJiraIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveJiraIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Jira
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/jira?next=/account/integrations`}>
                Connect to Jira
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Clickup"}
          logoAssetUrl={clickupLogo}
          text={
            <>
              <p>Connect your Topicflow and Clickup accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>Preview Clickup tasks in Topicflow meeting notes</li>
              </ul>
            </>
          }
          buttons={
            account.hasClickupAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveClickupIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveClickupIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Clickup
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/clickup?next=/account/integrations`}>
                Connect to Clickup
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Salesforce"}
          logoAssetUrl={salesforceLogo}
          text={
            <>
              <p>Connect your Topicflow and Salesforce accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Preview Salesforce Opportunities in Topicflow meeting notes
                </li>
                <li>Link Salesforce data to Topicflow KPIs</li>
              </ul>
            </>
          }
          buttons={
            account.hasSalesforceAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveSalesforceIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveSalesforceIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Salesforce
                </Button>
              </>
            ) : (
              <Button
                rounded
                url={`/sso/salesforce?next=/account/integrations`}
              >
                Connect to Salesforce
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Hubspot"}
          logoAssetUrl={hubspotLogo}
          text={
            <>
              <p>Connect your Topicflow and Hubspot accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>Preview Hubspot Deals in Topicflow meeting notes</li>
                <li>
                  Search for Hubspot Deals from the Explorer, and embed lists in
                  meeting notes
                </li>
                <li>Link Hubspot deal data to Topicflow KPIs</li>
              </ul>
            </>
          }
          buttons={
            account.hasHubspotAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveHubspotIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveHubspotIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Hubspot
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/hubspot?next=/account/integrations`}>
                Connect to Hubspot
              </Button>
            )
          }
        />
        {personalTranscriptSyncIntegrations.map((integration) => (
          <IntegrationRow
            key={integration.provider}
            logoAlt={integration.logoAlt}
            logoAssetUrl={integration.logoAssetUrl}
            text={integration.description}
            buttons={renderPersonalTranscriptSyncButtons(integration)}
          />
        ))}
        <IntegrationRow
          logoAlt={"Read.AI"}
          logoAssetUrl={readAiLogo}
          text={
            <>
              <p>Import your Read.AI meeting transcripts into Topicflow.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Import Read.AI transcripts into matching Topicflow meetings
                </li>
                <li>Create Topicflow topics from Read.AI meeting summaries</li>
              </ul>
            </>
          }
          buttons={
            account.hasReadAiAuth ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveReadAiIntegration();
                  }}
                >
                  <CheckIcon className="text-green-500 size-5" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={() => {
                    handleRemoveReadAiIntegration();
                  }}
                  className="font-light text-xs py-1 px-2 flex"
                >
                  Disconnect from Read.AI
                </Button>
              </>
            ) : (
              <Button rounded url={`/sso/readai?next=/account/integrations`}>
                Connect to Read.AI
              </Button>
            )
          }
        />
      </ul>
    </div>
  );
};

export default Integrations;
